import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { SH } from 'common/components';
import EIQDocumentPreview from 'common/components/document-preview/eiq-document-preview';
import InfoField from 'common/components/info-block/info-field/info-field';
import { DATE_SHORT_MMM_DD_YYYY, DATE_SHORT_MMM_D_YYYY_H_MMA } from 'common/constans/date.constants';
import { RentalDocumentTypeEnum, rentalDocumentTypeMap } from 'common/enums/rental-document-type.enum';
import { RentalManagementTypeEnum } from 'common/enums/rental-management-type.enum';
import { rentalPaymentStatusColorMap, rentalPaymentStatusMap } from 'common/enums/rental-payment-status.enum';
import { RentalPropertyStatusEnum, rentalPropertyStatusColorMap } from 'common/enums/rental-property-status.enum';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatRentalFee, formatRentalPropertyNumber } from 'common/helpers/rental-property.helper';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import rentalPropertyService from 'common/services/rental-property-service/rental-property.service';
import { IRentalPropertyDetailsRead } from 'common/services/rental-property-service/rental-property.service.types';
import configurationStore from 'common/stores/configuration/configuration.store';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as ApproveIcon } from 'assets/icons/approve.svg';
import { ReactComponent as RejectIcon } from 'assets/icons/reject.svg';
import ApproveRentalPropertyModal from '../approve-rental-property-modal/approve-rental-property-modal';
import RejectRentalPropertyModal from '../reject-rental-property-modal/reject-rental-property-modal';
import styles from '../rental-portal.module.scss';

const approvalStatusTextMap: Record<RentalPropertyStatusEnum, string> = {
  [RentalPropertyStatusEnum.Pending]: 'Awaiting decision from property manager.',
  [RentalPropertyStatusEnum.Approved]: 'Application approved.',
  [RentalPropertyStatusEnum.Rejected]: 'Application rejected.',
  [RentalPropertyStatusEnum.Expired]: 'Registration expired.',
};

export const RentalPropertyDetails = observer(() => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [property, setProperty] = useState<IRentalPropertyDetailsRead>();

  const loadProperty = useCallback(() => {
    const id = propertyId ? parseInt(propertyId, 10) : undefined;
    if (id !== undefined && !isNaN(id)) {
      rentalPropertyService.getRentalProperty(id).then(setProperty);
    }
  }, [propertyId]);

  useEffect(() => {
    loadProperty();
  }, [loadProperty]);

  const onReviewModalClose = (isSuccess?: boolean) => {
    if (isSuccess) {
      loadProperty();
    }
  };

  const approveModal = useModal({ onClose: onReviewModalClose });
  const rejectModal = useModal({ onClose: onReviewModalClose });

  if (!property) {
    return null;
  }

  const isPending = property.status === RentalPropertyStatusEnum.Pending;
  const statusLabel = isPending ? 'Pending Review' : property.status;
  const isAgencyManaged = property.managementType === RentalManagementTypeEnum.Agency;
  const rentalPortal = configurationStore.configuration?.rentalPortal;

  return (
    <div>
      <Card className={`eiq-card eiq-card-border ${styles.detailsCard}`} style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Space size={12}>
            <SH.HotkeyLink to="/eiq-manage/rental-portal/properties" className="go-back" label={<BackIcon />} />
            <div className={styles.headerTitleBlock}>
              <span className={styles.applicationNumber}>{formatRentalPropertyNumber(property.id)}</span>
              <h2 className="main-title">{`${formatAddressLabel(property.address)} — Property Registration`}</h2>
              <span className={styles.submittedLine}>
                {`Submitted ${dayjs(property.createdAt).format(DATE_SHORT_MMM_DD_YYYY)} · ${property.communityName}`}
              </span>
            </div>
          </Space>
          <Space size={8}>
            <Tag className="eiq-tag" color={rentalPropertyStatusColorMap[property.status]}>
              {statusLabel}
            </Tag>
            {isPending && (
              <>
                <Button
                  icon={<RejectIcon />}
                  size="middle"
                  type="primary"
                  className={styles.rejectButton}
                  onClick={rejectModal.showModal}
                  danger>
                  Reject
                </Button>
                <Button icon={<ApproveIcon />} size="middle" type="primary" onClick={approveModal.showModal}>
                  Approve
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Property</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Address" value={formatAddressLabel(property.address)} />
              <InfoField label="Community" value={property.communityName} />
              <InfoField label="County STR #" value={property.countyRegistrationNumber} />
              <InfoField
                label="Registration expiration"
                value={
                  property.registrationExpirationDate
                    ? dayjs(property.registrationExpirationDate).format(DATE_SHORT_MMM_DD_YYYY)
                    : 'One year from approval'
                }
              />
              <InfoField label="Maximum occupancy" value={property.maxOccupancy} />
              <InfoField
                label="Emergency contact"
                value={`${property.emergencyContactName} · ${property.emergencyContactPhone.fullNumber}`}
              />
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Owner</h3>
            <div className={styles.fieldsGrid}>
              <InfoField label="Name" value={property.ownerName} />
              <InfoField label="Email" value={property.ownerEmail ?? '—'} />
              <InfoField label="Phone" value={property.ownerPhone ?? '—'} />
            </div>
          </Card>
          {isAgencyManaged && (
            <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
              <h3 className={styles.sectionTitle}>Assigned agency</h3>
              <div className={styles.fieldsGrid}>
                <InfoField label="Agency" value={property.managingAgencyName ?? '—'} />
                <InfoField label="Primary contact" value={property.managingAgencyContactName ?? '—'} />
                <InfoField label="Email" value={property.managingAgencyEmail ?? '—'} />
              </div>
            </Card>
          )}
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Documents</h3>
            {property.documents.map((document) => (
              <div key={document.fileUri} className={styles.documentRow}>
                <div className={styles.documentInfo}>
                  <span className={styles.documentName}>
                    <FileTextOutlined />
                    {document.fileName}
                  </span>
                  <span className={styles.documentType}>{rentalDocumentTypeMap[document.type as RentalDocumentTypeEnum]}</span>
                </div>
                <EIQDocumentPreview
                  url={document.fileUri}
                  fileType={UploadFileTypes.RentalPropertyDocument}
                  fileName={document.fileName}
                  contentType={document.contentType}
                />
              </div>
            ))}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Payment</h3>
            <div className={styles.fieldsColumn}>
              <InfoField
                label="Registration fee"
                value={formatRentalFee(property.paymentStatus, rentalPortal?.isPropertyFeeEnabled, rentalPortal?.propertyRegistrationFee)}
              />
              <InfoField
                label="Status"
                value={
                  <Tag className="eiq-tag" color={rentalPaymentStatusColorMap[property.paymentStatus]}>
                    {rentalPaymentStatusMap[property.paymentStatus]}
                  </Tag>
                }
              />
              {property.externalPaymentId && (
                <InfoField
                  label="Payment ID"
                  value={
                    <Typography.Text copyable ellipsis={{ tooltip: property.externalPaymentId }}>
                      {property.externalPaymentId}
                    </Typography.Text>
                  }
                />
              )}
            </div>
          </Card>
          <Card className={`eiq-card ${styles.detailsCard} ${styles.sectionCard}`}>
            <h3 className={styles.sectionTitle}>Approval status</h3>
            <div className={styles.fieldsColumn}>
              <span className={styles.approvalStatusText}>
                {`Last updated ${dayjs(property.updatedAt).format(DATE_SHORT_MMM_D_YYYY_H_MMA)}. ${approvalStatusTextMap[property.status]}`}
              </span>
              {property.status === RentalPropertyStatusEnum.Rejected && property.rejectionComment && (
                <InfoField label="Rejection reason" value={<span className={styles.rejectionComment}>{property.rejectionComment}</span>} />
              )}
            </div>
          </Card>
        </Col>
      </Row>
      <RejectRentalPropertyModal
        initData={{ rentalPropertyId: property.id }}
        title="Reason for rejection"
        isOpen={rejectModal.isOpenModal}
        onClose={rejectModal.hideModal}
      />
      <ApproveRentalPropertyModal
        initData={{ rentalPropertyId: property.id }}
        title="Approve rental property"
        isOpen={approveModal.isOpenModal}
        onClose={approveModal.hideModal}
      />
    </div>
  );
});
